// useWebSocket.js
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';

export function useWebSocket({ onRunStarted, onRunCompleted, onRunStatus, onEntityCards }) {
    const [socketMessages, setSocketMessages] = useState([]);
    const [entityCardEvents, setEntityCardEvents] = useState([]);
    const socketRef = useRef<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    
    // Use refs to store latest callbacks to avoid recreating socket on callback changes
    const onEntityCardsRef = useRef(onEntityCards);
    const onRunStartedRef = useRef(onRunStarted);
    const onRunCompletedRef = useRef(onRunCompleted);
    const onRunStatusRef = useRef(onRunStatus);
    
    // Update refs when callbacks change
    useEffect(() => {
        onEntityCardsRef.current = onEntityCards;
        onRunStartedRef.current = onRunStarted;
        onRunCompletedRef.current = onRunCompleted;
        onRunStatusRef.current = onRunStatus;
    }, [onEntityCards, onRunStarted, onRunCompleted, onRunStatus]);
    
    // Get API URL from sessionStorage and listen for changes
    const [rawApiUrl, setRawApiUrl] = useState(() => sessionStorage.getItem('selectedEnvUrl') || '');
    
    // Determine the actual URL to use for WebSocket connection
    // In local dev, if rawApiUrl points directly to backend (localhost:5001), 
    // use the frontend's origin to route through Vite proxy for WebSockets
    const socketBaseUrl = useMemo(() => {
      // @ts-ignore - import.meta.env.DEV is a Vite environment variable
      const isDev = import.meta.env?.DEV || process.env.NODE_ENV === 'development';
      
      if (isDev && rawApiUrl && (rawApiUrl.includes('localhost:5001') || rawApiUrl.includes('127.0.0.1:5001'))) {
        console.log('🔄 [WebSocket] Local dev detected - routing through Vite proxy');
        return window.location.origin; // Use frontend origin (localhost:5173) to go through proxy
      }
      return rawApiUrl;
    }, [rawApiUrl]);
    
    // Listen for sessionStorage changes (when user selects a different environment)
    useEffect(() => {
      const handleStorageChange = () => {
        const newUrl = sessionStorage.getItem('selectedEnvUrl') || '';
        if (newUrl !== rawApiUrl) {
          console.log('🔄 [WebSocket] Environment URL changed:', newUrl);
          setRawApiUrl(newUrl);
        }
      };
      
      // Check periodically since sessionStorage events don't fire in same tab
      const interval = setInterval(handleStorageChange, 1000);
      window.addEventListener('storage', handleStorageChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('storage', handleStorageChange);
      };
    }, [rawApiUrl]);

    // Clear accumulated messages (call this on conversation reset)
    const clearSocketMessages = useCallback(() => {
      console.log('🧹 [WebSocket] Clearing accumulated socket messages');
      setSocketMessages([]);
      setEntityCardEvents([]);
    }, []);

    useEffect(() => {
      // Don't connect if no API URL is set
      if (!socketBaseUrl) {
        console.log('⏳ [WebSocket] Waiting for API URL to be set...');
        return;
      }
      
      // Construct socket URL - use socketBaseUrl which may route through Vite proxy in dev
      // The namespace is /aiAssistant for the AI assistant gateway
      const socketUrl = socketBaseUrl + '/aiAssistant';
      
      console.log('🔌 [WebSocket] Connecting to:', socketUrl);
      
      // Initialize the socket with reconnection options
      const socket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });
      socketRef.current = socket;
      
      // Connection events
      socket.on('connect', () => {
        console.log('✅ [WebSocket] Connected to AI Assistant gateway');
        console.log(`   Socket ID: ${socket.id}`);
        console.log(`   URL: ${socketUrl}`);
        console.log(`   Transport: ${socket.io.engine.transport.name}`);
        setIsConnected(true);
      });
      
      socket.on('disconnect', (reason) => {
        console.log('🔌 [WebSocket] Disconnected:', reason);
        console.log(`   Socket ID was: ${socket.id}`);
        console.log(`   Disconnect reason: ${reason}`);
        setIsConnected(false);
      });
      
      socket.on('connect_error', (error) => {
        console.error('❌ [WebSocket] Connection error:', error.message);
        console.error(`   Error type: ${error.type}`);
        console.error(`   Error details: ${JSON.stringify(error)}`);
        setIsConnected(false);
      });
      
      socket.on('reconnect', (attemptNumber) => {
        console.log(`🔄 [WebSocket] Reconnected after ${attemptNumber} attempts`);
        console.log(`   New Socket ID: ${socket.id}`);
      });
      
      socket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`🔄 [WebSocket] Reconnection attempt ${attemptNumber}`);
      });
      
      socket.on('reconnect_error', (error) => {
        console.error(`❌ [WebSocket] Reconnection error:`, error.message);
      });
      
      socket.on('reconnect_failed', () => {
        console.error('❌ [WebSocket] Reconnection failed after all attempts');
      });
  
      // Listen for new messages from the server
      socket.on('assistantMessage', (message) => {
        console.log('📩 [WebSocket] Received assistant message:', message);
        setSocketMessages((prevMessages) => [...prevMessages, message]);
      });

      // Listen for entity cards (structured data for hover cards)
      socket.on('assistant.entityCards', (data) => {
        console.log('🃏 [WebSocket] Received entity cards from server');
        console.log(`   Connection state: ${socket.connected ? 'CONNECTED' : 'DISCONNECTED'}`);
        console.log(`   Socket ID: ${socket.id}`);
        console.log(`   Message ID: ${data.messageId}`);
        console.log(`   Cards: ${Object.keys(data.entityCards || {}).join(', ')}`);
        console.log(`   Full data: ${JSON.stringify(data, null, 2)}`);
        
        if (!data || !data.messageId) {
          console.error('❌ [WebSocket] Invalid entityCards data received:', data);
          return;
        }
        
        if (!data.entityCards || Object.keys(data.entityCards).length === 0) {
          console.warn('⚠️ [WebSocket] Entity cards data is empty');
        }
        
        setEntityCardEvents((prevEvents) => {
          const updated = [...prevEvents, data];
          console.log(`   Total entity card events stored: ${updated.length}`);
          return updated;
        });
        
        // Use ref to get latest callback without recreating socket
        const currentCallback = onEntityCardsRef.current;
        if (currentCallback) {
          console.log('   Calling onEntityCards callback (from ref)...');
          try {
            currentCallback(data);
            console.log('   ✅ onEntityCards callback executed successfully');
          } catch (error) {
            console.error('   ❌ Error in onEntityCards callback:', error);
            console.error(`   Error details: ${error.message}`);
            console.error(`   Stack: ${error.stack}`);
          }
        } else {
          console.warn('   ⚠️ onEntityCards callback is not defined');
        }
      });

      // Listen for run lifecycle events
      socket.on('runStarted', (message) => {
        console.log('🚀 [WebSocket] Run Started', message);
        const callback = onRunStartedRef.current;
        if (callback) callback(message);
      });

      socket.on('runStatusMessage', (message) => {
        console.log('📊 [WebSocket] Run Status', message);
        const callback = onRunStatusRef.current;
        if (callback) callback(message);
      });
  
      socket.on('runCompleted', (message) => {
        console.log('✅ [WebSocket] Run Completed', message);
        const callback = onRunCompletedRef.current;
        if (callback) callback(message);
      });

      socket.on('error', (err) => {
        console.error('❌ [WebSocket] Socket error:', err);
      });
  
      // Cleanup on unmount or when socketBaseUrl changes
      return () => {
        console.log('🔌 [WebSocket] Disconnecting...');
        socket.disconnect();
        setIsConnected(false);
      };
    }, [socketBaseUrl]); // Only recreate socket when URL changes, not when callbacks change
  
    const sendMessage = useCallback((message) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('userMessage', { message });
      } else {
        console.warn('⚠️ [WebSocket] Cannot send message - not connected');
      }
    }, []);
  
    return { socketMessages, sendMessage, entityCardEvents, clearSocketMessages, isConnected };
  }